import { Particle } from '../ParticleLayer';

const CONFIG = {
    baseScale: 0.45,
    unearthDuration: 350,
    impactDuration: 250,
    defendDuration: 1200,
    dissipateDuration: 600,
    paths: {
        shield: '/assets/effects/steel_shield/shield.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.shield);

// ==========================================
// PHYSICAL PARTICLES (No Magic)
// ==========================================

class GroundDust {
    x: number; y: number;
    vx: number; vy: number;
    life: number = 0;
    maxLife: number;
    size: number;
    color: string;

    constructor(x: number, y: number, isOpponent = false) {
        const yDir = isOpponent ? -1 : 1;
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 6;
        this.x = x + Math.cos(angle) * 30;
        this.y = y + 40 * yDir + Math.sin(angle) * 15;

        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * (speed * 0.4) - (1 + Math.random() * 3) * yDir;

        this.maxLife = 500 + Math.random() * 400;
        this.size = 15 + Math.random() * 35; // Thicker dust than Ancient Shield

        const colors = ['#1e293b', '#334155', '#475569', '#0f172a']; // Dirt & stone colors
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.85; // Heavy friction
        this.vy *= 0.85;
        this.size += dt * 0.08;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const alpha = Math.max(0, (1 - progress) * 0.6);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
        ctx.restore();
    }
}

class ClashingSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number = 0;
    maxLife: number;
    size: number;
    color: string;

    // Sparks from metal weapons hitting the steel shield
    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 80;
        this.y = y + (Math.random() - 0.5) * 120;

        this.vx = (Math.random() - 0.5) * 8;
        this.vy = (Math.random() - 0.5) * 8; // Explosive omnidirectional sparks

        this.maxLife = 200 + Math.random() * 300; // Very short life, intense
        this.size = 1 + Math.random() * 4;

        const colors = ['#fde047', '#fbbf24', '#f97316', '#ffffff']; // Orange/yellow hot metal
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.9;
        this.vy += 0.5; // Gravity pulls them down fast
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const alpha = Math.max(0, 1 - (this.life / this.maxLife));

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';

        ctx.beginPath();
        // Draw spark as a stretched line in direction of velocity
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.x - this.vx * 2, this.y - this.vy * 2);

        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.size;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.restore();
    }
}

class HeavyShrapnel {
    x: number; y: number;
    vx: number; vy: number;
    rotation: number;
    rotSpeed: number;
    life: number = 0;
    maxLife: number;
    size: number;
    color: string;

    constructor(x: number, y: number, isOpponent = false) {
        const yDir = isOpponent ? -1 : 1;
        this.x = x + (Math.random() - 0.5) * 60;
        this.y = y + (Math.random() - 0.5) * 100;

        const angle = Math.random() * Math.PI * 2;
        const speed = 5 + Math.random() * 12; // Extremely fast initial burst
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 6 * yDir; // Throws upwards then falls

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.8;

        this.maxLife = 400 + Math.random() * 300;
        this.size = 8 + Math.random() * 20; // Large chunks

        const colors = ['#1e293b', '#334155', '#475569', '#64748b', '#0f172a'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number) {
        this.life += dt;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.96;
        this.vy += 0.8; // High gravity for heavy steel
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= this.maxLife) return;
        const progress = this.life / this.maxLife;
        const alpha = Math.max(0, 1 - progress);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.beginPath();
        // Jagged, heavy metal plate shape
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size * 0.8, -this.size * 0.3);
        ctx.lineTo(this.size * 0.5, this.size * 0.8);
        ctx.lineTo(-this.size * 0.3, this.size);
        ctx.lineTo(-this.size, 0);
        ctx.closePath();

        ctx.fillStyle = this.color;
        ctx.fill();

        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.restore();
    }
}

export type SteelShieldEffect = {
    kind: 'steelShield'
    t: number
    tx: number; ty: number;
    isOpponent?: boolean;
    data?: {
        phase: 'UNEARTH' | 'IMPACT' | 'DEFEND' | 'SHATTER'
        dusts: GroundDust[]
        sparks: ClashingSpark[]
        shrapnel: HeavyShrapnel[]
        impactFired: boolean
        shatterFired: boolean
    }
}

export const createSteelShieldEffect = (tx: number, ty: number, isOpponent = false): SteelShieldEffect => ({
    kind: 'steelShield', t: 0, tx, ty, isOpponent
});

export const renderSteelShieldEffect = (
    eff: SteelShieldEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        eff.data = {
            phase: 'UNEARTH',
            dusts: [], sparks: [], shrapnel: [],
            impactFired: false,
            shatterFired: false
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tUnearth = CONFIG.unearthDuration;
    const tImpact = tUnearth + CONFIG.impactDuration;
    const tDefend = tImpact + CONFIG.defendDuration;
    const tTotal = tDefend + CONFIG.dissipateDuration;

    // Easing Functions
    const easeOutCubic = (x: number): number => 1 - Math.pow(1 - x, 3);
    const easeInCubic = (x: number): number => x * x * x;
    const easeOutBackHeavy = (x: number): number => {
        const c1 = 1.2; // Less bouncy than ancient shield, much heavier thud
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    };

    if (eff.t < tUnearth) d.phase = 'UNEARTH';
    else if (eff.t < tImpact) d.phase = 'IMPACT';
    else if (eff.t < tDefend) d.phase = 'DEFEND';
    else d.phase = 'SHATTER';

    ctx.save();

    let shieldAlpha = 1;
    let currentScale = CONFIG.baseScale;
    let shieldY = eff.ty;
    let visualShakeX = 0;
    let visualShakeY = 0;

    const yDir = eff.isOpponent ? -1 : 1;

    // --- PHASE LOGIC ---

    if (d.phase === 'UNEARTH') {
        const p = eff.t / tUnearth;

        shieldAlpha = Math.min(1, p * 4); // Immediate presence

        // Rises slower and heavier than the ancient shield
        const targetY = eff.ty - 40 * yDir; // Barely overshoots
        const startY = eff.ty + 150 * yDir;
        shieldY = startY + (targetY - startY) * easeOutCubic(p);

        currentScale = CONFIG.baseScale * (1 + 0.2 * p); // Less exaggerated scale-in

        // Spawn constant heavy dust while unearthing
        if (Math.random() < 0.9) {
            d.dusts.push(new GroundDust(eff.tx, eff.ty, eff.isOpponent));
            d.dusts.push(new GroundDust(eff.tx, eff.ty, eff.isOpponent));
        }

    } else if (d.phase === 'IMPACT') {
        const p = (eff.t - tUnearth) / CONFIG.impactDuration;
        shieldAlpha = 1;

        // Slams down hard
        const startY = eff.ty - 40 * yDir;
        const targetY = eff.ty; // Locks solid
        shieldY = startY + (targetY - startY) * easeOutBackHeavy(p);

        currentScale = CONFIG.baseScale * (1.2 - 0.2 * easeOutBackHeavy(p));

        // Violent Screen Shake
        if (p < 0.8) {
            visualShakeX = (Math.random() - 0.5) * 25 * (1 - p); // Stronger horizontal shake
            visualShakeY = (Math.random() - 0.5) * 10 * (1 - p);
        }

        if (!d.impactFired) {
            d.impactFired = true;
            // Massive dust explosion
            for (let i = 0; i < 40; i++) d.dusts.push(new GroundDust(eff.tx, eff.ty, eff.isOpponent));
            // A few clashing sparks from the sheer force of the metal impacting stone
            for (let i = 0; i < 15; i++) d.sparks.push(new ClashingSpark(eff.tx, eff.ty));
        }

    } else if (d.phase === 'DEFEND') {
        shieldAlpha = 1;
        shieldY = eff.ty;
        currentScale = CONFIG.baseScale;

        // Zero bobbing. Pure physical weight taking hits.

        // Occasional weapon clashes against the steel
        if (Math.random() < 0.05) { // 5% chance per frame for a hit
            for (let i = 0; i < 10; i++) d.sparks.push(new ClashingSpark(eff.tx, eff.ty));
        }

    } else if (d.phase === 'SHATTER') {
        const p = (eff.t - tDefend) / CONFIG.dissipateDuration;

        // Does not scale down smoothly. It instantly snaps and drops.
        shieldAlpha = p < 0.1 ? 1 : 0; // Flash disappears

        if (!d.shatterFired) {
            d.shatterFired = true;
            // Bursts into large heavy metal plates
            for (let i = 0; i < 50; i++) d.shrapnel.push(new HeavyShrapnel(eff.tx, eff.ty, eff.isOpponent));
            for (let i = 0; i < 20; i++) d.dusts.push(new GroundDust(eff.tx, eff.ty, eff.isOpponent));
        }
    }

    // --- RENDER ORDER ---

    // 1. Heavy Dust
    d.dusts = d.dusts.filter(s => s.life < s.maxLife);
    d.dusts.forEach(s => { s.update(dt); s.draw(ctx); });

    // 2. Main Steel Shield
    if (shieldAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = shieldAlpha;

        // Offsets exactly match the Ancient Shield to maintain symmetric physical placement
        const localOffsetX = eff.isOpponent ? 0 : 200;
        const localOffsetY = eff.isOpponent ? 0 : 230;

        ctx.translate(eff.tx + visualShakeX + localOffsetX, shieldY + visualShakeY + localOffsetY);
        ctx.scale(currentScale, currentScale);

        if (!eff.isOpponent) {
            ctx.rotate(Math.PI);
        }

        const img = getSprite(CONFIG.paths.shield);
        if (img.complete && img.naturalWidth > 0) {
            // Tight, dark physical shadow
            ctx.shadowColor = 'rgba(0,0,0,0.95)';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetY = eff.isOpponent ? 10 : -10;

            // Visual center offset since the shield design is very top-heavy
            const offsetY = eff.isOpponent ? 0 : 350;

            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2 + offsetY);
        }
        ctx.restore();
    }

    // 3. Foreground Sparks & Shrapnel
    d.sparks = d.sparks.filter(s => s.life < s.maxLife);
    d.sparks.forEach(s => { s.update(dt); s.draw(ctx); });

    d.shrapnel = d.shrapnel.filter(s => s.life < s.maxLife);
    d.shrapnel.forEach(s => { s.update(dt); s.draw(ctx); });

    ctx.restore();

    return eff.t < tTotal;
};
